<template>
    <!-- Permission denied banner -->
    <div v-if="!skillAccess.canAccess && targetUserInfo" class="alert alert-warning d-flex align-items-center gap-2 mx-4 mt-3">
        <i class="material-icons">lock</i>
        <span>{{ skillAccess.reason }}</span>
    </div>

    <div class="mb-4">
        <Table :rows="existingRules" :columns="columns" idKey="rule_number" :searchable=false :loading="loading"
            :pagination="false" :showNoDataFoundImage="false">
            <template #col-description="{ row }">
                <span v-html="row.description"></span>
            </template>

            <!-- Row actions -->
            <template #actions="{ row }">
                <button v-if="skillAccess.canAccess" class="btn btn-sm my-0 py-0" @click="deleteRule(row)">
                    <i class="material-icons text-lg text-danger" color="#FF0000">delete</i>
                </button>
            </template>
        </Table>
    </div>

    <Rules v-if="skillAccess.canAccess" ref="rulesRef" :actionOptions="actionOptions" :initialRules="rules" :savingRuleIds="savingRuleIds"
        :savingAll="savingAll" @save="onSave" @info="openRuleInfo" :loading="loading" :saveAllAllowed="false" />

    <RuleInfoModal v-model:show="showRuleInfo" :ruleId="infoRuleId" ruleContext="user" />
</template>

<script setup>
import { ref, computed, onMounted, inject } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { useToast } from "vue-toast-notification";
import { getLocationId, getLogginedPersonId } from "@/utils/SessionDataHandler";
import Rules from "@/components/Rules/Rules.vue";
import RuleInfoModal from "@/components/Rules/RuleInfoModal.vue";
import Table from "@/components/Table/Table.vue";
import { usePermissions } from "@/composables/usePermissions";

const props = defineProps({
    targetUserInfo: { type: Object, default: null },
    targetPersonId: { type: [Number, String], default: null },
    groupAdminEdit: { type: Boolean, default: false },
    peopleGroupId: { type: [Number, String], default: null },
});

/* -------------------------------
   Store + Routing + Toast
-------------------------------- */
const store = useStore();
const route = useRoute();
const toast = useToast();
const { canAccessUserBySkill } = usePermissions();

const skillAccess = computed(() => {
    if (props.groupAdminEdit) return { canAccess: true, reason: "" };
    if (!props.targetUserInfo) return { canAccess: true, reason: "" };
    return canAccessUserBySkill(props.targetUserInfo);
});

// Get personId from inject (modal), route params, or session data
const _injectedPersonId = inject('editUserPersonId', null);
const getPersonId = () => {
    if (props.targetPersonId) {
        return Number(props.targetPersonId);
    }
    if (_injectedPersonId?.value) {
        return Number(_injectedPersonId.value);
    }
    if (route.params.userid) {
        return Number(route.params.userid);
    }
    // Fallback to session data if route param is not available
    const sessionPersonId = getLogginedPersonId() || store.getters.stSessionData?.personId || store.getters.stSessionData?.personid;
    return sessionPersonId ? Number(sessionPersonId) : null;
};

const personId = getPersonId();
const locationId = getLocationId();
const injectedPeopleGroupId = inject('editUserPeopleGroupId', null);
const peopleGroupId = computed(() => {
    const value = props.peopleGroupId ?? injectedPeopleGroupId?.value ?? null;
    return value ? Number(value) : null;
});

const loading = ref(true);

/* -------------------------------
   State
-------------------------------- */
const rules = ref([]);
const rulesRef = ref(null);
const savingRuleIds = ref([]);
const savingAll = ref(false);
const existingRules = ref([]);
const showRuleInfo = ref(false);
const infoRuleId = ref(null);

function openRuleInfo(ruleId) {
    infoRuleId.value = ruleId;
    showRuleInfo.value = true;
}

const columns = [
    { key: "ruleid", label: "ID" },
    { key: "name", label: "Name" },
    { key: "description", label: "Description" },
];

// Default action options (used as fallback)
const actionOptions = [
    { value: "never", label: "Never" },
    { value: "when-possible", label: "When Possible" },
];

// Per-rule action options configuration
const ruleActionOptions = {
    1: [{ value: "never", label: "Never" }, { value: "when-possible", label: "When Possible" }],
    2: [{ value: "never", label: "Never" }, { value: "when-possible", label: "When Possible" }],
    3: [{ value: "never", label: "Never" }, { value: "when-possible", label: "When Possible" }],
    4: [{ value: "when-possible", label: "When Possible" }],
    5: [{ value: "never", label: "Never" }],
    6: [{ value: "when-possible", label: "When Possible" }],
    7: [{ value: "when-possible", label: "When Possible" }],
    8: [{ value: "never", label: "Never" }],
};

/* -------------------------------
   Mapper: API -> UI Rules
-------------------------------- */
const mapSchedulingRules = (
    templates,
    availableWorkgroups,
    persons,
    peopleGroups = []
) => {
    const getPeopleTargetOptions = () => [
        ...persons.map((person) => ({
            label: `Person: ${person.firstname} ${person.lastname}`,
            value: `person:${person.personid}`,
            targetType: "person",
        })),
        ...peopleGroups.map((group) => ({
            label: `People Group: ${group.name}`,
            value: `group:${group.id}`,
            targetType: "group",
        })),
    ];

    // Helper function to create workgroup options (creates a new array each time)
    const getWorkgroupOptions = () => availableWorkgroups.map((wg) => ({
        label: wg.workgroup_name,
        value: wg.workgroup_id,
    }));

    return templates
        .map((tpl) => {
            switch (tpl.rule_number) {
                case 1:
                    return {
                        id: tpl.rule_number,
                        action: "never",
                        name: tpl.name,
                        descriptionHtml: tpl.description,
                        type: tpl.type,
                        actionOptions: ruleActionOptions[1],
                        inputFields: [
                            {
                                key: "days",
                                type: "number",
                                modelValue: tpl.parameters.days,
                                suffix: "day(s) of the last schedule for",
                                useSpinner: true,
                                spinnerId: "userrule1value1",
                                min: 1,
                                max: 90,
                            },
                            {
                                key: "persons",
                                type: "multi",
                                multiple: true,
                                modelValue: Array.isArray(tpl.parameters.persons) ? [...tpl.parameters.persons] : [],
                                options: getPeopleTargetOptions(),
                            },
                        ],
                    };
                case 2:
                    return {
                        id: tpl.rule_number,
                        action: "never",
                        name: tpl.name,
                        type: tpl.type,
                        descriptionHtml: tpl.description,
                        actionOptions: ruleActionOptions[2],
                        inputFields: [
                            {
                                key: "persons_rule2",
                                type: "multi",
                                multiple: true,
                                modelValue: [],
                                options: getPeopleTargetOptions(),
                            },
                        ],
                    };
                case 3:
                    return {
                        id: tpl.rule_number,
                        action: "never",
                        name: tpl.name,
                        type: tpl.type,
                        descriptionHtml: tpl.description,
                        actionOptions: ruleActionOptions[3],
                        inputFields: [
                            {
                                key: "persons_rule3",
                                type: "multi",
                                multiple: true,
                                modelValue: [],
                                options: getPeopleTargetOptions(),
                            },
                        ],
                    };
                case 4:
                    return {
                        id: tpl.rule_number,
                        action: "when-possible",
                        name: tpl.name,
                        type: tpl.type,
                        descriptionHtml: tpl.description,
                        actionOptions: ruleActionOptions[4],
                        inputFields: [
                            {
                                key: "workgroup",
                                type: "select",
                                modelValue: tpl.parameters.workgroup_id,
                                options: getWorkgroupOptions(),
                            },
                        ],
                    };
                case 5:
                    return {
                        id: tpl.rule_number,
                        action: "never",
                        name: tpl.name,
                        type: tpl.type,
                        descriptionHtml: tpl.description,
                        actionOptions: ruleActionOptions[5],
                        inputFields: [
                            {
                                key: "times",
                                type: "number",
                                modelValue: tpl.parameters.max_times,
                                suffix: "time(s) within",
                                useSpinner: true,
                                spinnerId: "userrule5value1",
                            },
                            {
                                key: "days",
                                type: "number",
                                modelValue: tpl.parameters.days,
                                suffix: "day(s)",
                                useSpinner: true,
                                spinnerId: "userrule5value2",
                            },
                        ],
                    };
                case 6:
                    return {
                        id: tpl.rule_number,
                        action: "when-possible",
                        name: tpl.name,
                        type: tpl.type,
                        descriptionHtml: tpl.description,
                        actionOptions: ruleActionOptions[6],
                        inputFields: [
                            {
                                key: "times",
                                type: "number",
                                modelValue: tpl.parameters.max_times,
                                suffix: "time(s) within",
                                useSpinner: true,
                                spinnerId: "userrule6value1",
                            },
                            {
                                key: "days",
                                type: "number",
                                modelValue: tpl.parameters.days,
                                suffix: "day(s) for Workgroup:",
                                useSpinner: true,
                                spinnerId: "userrule6value2",
                            },
                            {
                                key: "workgroup",
                                type: "select",
                                modelValue: tpl.parameters.workgroup_id,
                                options: getWorkgroupOptions(),
                            },
                        ],
                    };
                case 7:
                    return {
                        id: tpl.rule_number,
                        action: "when-possible",
                        name: tpl.name,
                        type: tpl.type,
                        descriptionHtml: tpl.description,
                        actionOptions: ruleActionOptions[7],
                        inputFields: [
                            {
                                key: "workgroup1",
                                type: "select",
                                modelValue: tpl.parameters.workgroup1_id,
                                options: getWorkgroupOptions(),
                                suffix: "AND",
                            },
                            {
                                key: "workgroup2",
                                type: "select",
                                modelValue: tpl.parameters.workgroup2_id,
                                options: getWorkgroupOptions(),
                                suffix: "within a",
                            },
                            {
                                key: "days",
                                type: "number",
                                modelValue: tpl.parameters.days,
                                suffix: "day span",
                                useSpinner: true,
                                spinnerId: "userrule7value1",
                            },
                        ],
                    };
                case 8:
                    return {
                        id: tpl.rule_number,
                        action: "never",
                        name: tpl.name,
                        type: tpl.type,
                        descriptionHtml: tpl.description,
                        actionOptions: ruleActionOptions[8],
                        inputFields: [
                            {
                                key: "times",
                                type: "number",
                                modelValue: tpl.parameters.max_times,
                                suffix: "time(s) within",
                                useSpinner: true,
                                spinnerId: "userrule8value1",
                            },
                            {
                                key: "days",
                                type: "number",
                                modelValue: tpl.parameters.days,
                                suffix: "day(s) for Workgroup:",
                                useSpinner: true,
                                spinnerId: "userrule8value2",
                            },
                            {
                                key: "workgroup",
                                type: "select",
                                modelValue: tpl.parameters.workgroup_id,
                                options: getWorkgroupOptions(),
                            },
                        ],
                    };
                default:
                    return null;
            }
        })
        .filter(Boolean);
};

// const buildSchedulingRule = (uiRule) => {
//     console.log("uiRule", uiRule);

//     const fields = uiRule.fields ?? {};

//     switch (uiRule.id) {
//         case 1:
//             return {
//                 rule_number: 1,
//                 name: "Schedule Within Days",
//                 description: "Schedule me within X day(s) of the last schedule for specific person(s)",
//                 type: "person_days",
//                 parameters: {
//                     days: fields.days,
//                     persons: (fields.persons || []).map(p => p.value),
//                 }
//             };

//         case 2:
//             return {
//                 rule_number: 2,
//                 name: "Do Not Schedule With Person(s)",
//                 description: "Prevent scheduling together with selected person(s)",
//                 type: "person_exclusion",
//                 parameters: {
//                     persons: (fields.persons || []).map(p => p.value),
//                 }
//             };

//         case 3:
//             return {
//                 rule_number: 3,
//                 name: "Only Schedule With Person(s)",
//                 description: "Allow scheduling only with selected person(s)",
//                 type: "person_inclusion",
//                 parameters: {
//                     persons: (fields.persons || []).map(p => p.value),
//                 }
//             };

//         case 4:
//             return {
//                 rule_number: 4,
//                 name: "Workgroup Restriction",
//                 description: "Restrict scheduling by workgroup",
//                 type: "workgroup_restriction",
//                 parameters: {
//                     workgroup_id: fields.workgroup
//                 }
//             };

//         case 5:
//             return {
//                 rule_number: 5,
//                 name: "Max Times in Period",
//                 description: "Limit scheduling to X times within Y days",
//                 type: "frequency_limit",
//                 parameters: {
//                     max_times: fields.times,
//                     days: fields.days
//                 }
//             };

//         case 6:
//             return {
//                 rule_number: 6,
//                 name: "Max Times in Period by Workgroup",
//                 description: "Limit scheduling by workgroup to X times within Y days",
//                 type: "frequency_limit_workgroup",
//                 parameters: {
//                     max_times: fields.times,
//                     days: fields.days,
//                     workgroup_id: fields.workgroup
//                 }
//             };

//         case 7:
//             return {
//                 rule_number: 7,
//                 name: "Workgroup Separation",
//                 description: "Do not schedule these workgroups together within X days",
//                 type: "workgroup_separation",
//                 parameters: {
//                     workgroup1_id: fields.workgroup1,
//                     workgroup2_id: fields.workgroup2,
//                     days: fields.days
//                 }
//             };

//         case 8:
//             return {
//                 rule_number: 8,
//                 name: "Max Times with Workgroup",
//                 description: "Limit scheduling with this workgroup to X times within Y days",
//                 type: "frequency_limit_workgroup",
//                 parameters: {
//                     max_times: fields.times,
//                     days: fields.days,
//                     workgroup_id: fields.workgroup
//                 }
//             };

//         default:
//             return null;
//     }
// };


const buildSchedulingRule = (uiRule) => {
    if (!uiRule) return null;

    // find template from API response by rule number
    const template = rules.value.find(
        (tpl) => tpl.id === uiRule.id
    );

    if (!template) {
        console.warn(`No matching rule template found for rule_number ${uiRule.id}`);
        return null;
    }

    const fields = uiRule.fields ?? {};
    const getPeopleTargets = (selection) => {
        const selectedTargets = Array.isArray(selection) ? selection : [];
        return {
            persons: selectedTargets
                .filter((target) => target?.targetType === "person")
                .map((target) => Number(String(target.value).replace("person:", ""))),
            people_group_ids: selectedTargets
                .filter((target) => target?.targetType === "group")
                .map((target) => Number(String(target.value).replace("group:", ""))),
        };
    };

    // now build parameters according to your existing pattern
    let parameters = {};

    switch (uiRule.id) {
        case 1:
            parameters = {
                days: fields.days,
                ...getPeopleTargets(fields.persons),
            };
            break;

        case 2:
            parameters = getPeopleTargets(fields.persons_rule2);
            break;

        case 3:
            parameters = getPeopleTargets(fields.persons_rule3);
            break;

        case 4:
            parameters = {
                workgroup_id: fields.workgroup,
            };
            break;

        case 5:
            parameters = {
                max_times: fields.times,
                days: fields.days,
            };
            break;

        case 6:
            parameters = {
                max_times: fields.times,
                days: fields.days,
                workgroup_id: fields.workgroup,
            };
            break;

        case 7:
            parameters = {
                workgroup1_id: fields.workgroup1,
                workgroup2_id: fields.workgroup2,
                days: fields.days,
            };
            break;

        case 8:
            parameters = {
                max_times: fields.times,
                days: fields.days,
                workgroup_id: fields.workgroup,
            };
            break;

        default:
            parameters = fields;
    }

    // merge dynamic data from API with parameters
    return {
        // rule_number: template.rule_number,
        rule_number: template.id,
        name: template.name,
        // description: template.description,
        description: template.descriptionHtml,
        type: template.type,
        condition: uiRule.action,
        parameters,
    };
};

/* -------------------------------
   Fetch Existing Rules
-------------------------------- */
const fetchExistingRules = async () => {
    try {
        const res = await store.dispatch("fetchSchedulingPreferences", {
            personId,
            locationId,
            peopleGroupId: peopleGroupId.value,
        });
        existingRules.value = res.existing_rules || [];
    } catch (err) {
        console.error("Failed to fetch existing rules:", err);
    }
};

/* -------------------------------
   Lifecycle: Fetch on mount
-------------------------------- */
onMounted(async () => {
    loading.value = true;
    try {
        const res = await store.dispatch("fetchSchedulingPreferences", {
            personId,
            locationId,
            peopleGroupId: peopleGroupId.value,
        });

        rules.value = mapSchedulingRules(
            res.rule_templates,
            res.available_workgroups,
            res.persons,
            res.people_groups
        );

        existingRules.value = res.existing_rules || [];
    }
    finally {
        loading.value = false;
    }
});

/* -------------------------------
   Save Single Rule
-------------------------------- */
const onSave = async (payload) => {

    if (!savingRuleIds.value.includes(payload.id)) {
        savingRuleIds.value.push(payload.id);
    }

    try {
        const rule = buildSchedulingRule(payload);

        var res = await store.dispatch("saveRule", {
            personid: personId,
            locationid: locationId,
            people_group_id: peopleGroupId.value,
            rule
        });


        toast.success(res.message, { position: "top-right" });

        savingRuleIds.value = savingRuleIds.value.filter(id => id !== payload.id);

        // Clear the specific rule fields using the child component's method
        if (rulesRef.value) {
            rulesRef.value.clearRuleFields(payload.id);
        }

        await fetchExistingRules();

    } catch (err) {
        savingRuleIds.value = savingRuleIds.value.filter(id => id !== payload.id);
        toast.error(err.message, { position: "top-right" });
    }
};

/* -------------------------------
   Save All Rules
-------------------------------- */
// const onSaveAll = async (payload) => {
//     try {
//         savingAll.value = true;
//         const apiRules = payload.map((r) => buildSchedulingRule(r));
//         const res = await store.dispatch("saveAllRules", {
//             personid: personId,
//             locationid: locationId,
//             rules: apiRules,
//         });
//         toast.success(res.message, { position: "top-right" });

//         // Clear all rule fields using the child component's method
//         if (rulesRef.value) {
//             rulesRef.value.clearAllRuleFields();
//         }

//         await fetchExistingRules();

//     } catch (err) {
//         toast.error(err.message, { position: "top-right" });
//     } finally {
//         savingAll.value = false;
//     }
// };

/* -------------------------------
   Delete Rule
-------------------------------- */
const deleteRule = async (row) => {
    try {
        const response = await store.dispatch("removeRule", {
            personid: personId,
            locationid: locationId,
            people_group_id: peopleGroupId.value,
            ruleidsarray: [row.ruleid],
        });

        if (response.success) {
            toast.success(response.message || "Rule removed successfully");

            // Refetch data from API to ensure sync
            loading.value = true;
            try {
                const res = await store.dispatch("fetchSchedulingPreferences", {
                    personId,
                    locationId,
                    peopleGroupId: peopleGroupId.value,
                });

                rules.value = mapSchedulingRules(
                    res.rule_templates,
                    res.available_workgroups,
                    res.persons,
                    res.people_groups
                );

                existingRules.value = res.existing_rules || [];
            } catch (err) {
                console.error("Failed to refetch rules after deletion:", err);
            } finally {
                loading.value = false;
            }
        } else {
            toast.error(response.message || "Failed to remove rule");
        }
    } catch (error) {
        toast.error(error.message || "Error removing rule");
    }
};
</script>
